import { useRef, useState } from 'react';
import { uploadToCloudinary } from '../lib/api';
import { toast } from 'sonner';
import { Camera, Loader2, X, ImagePlus, Trash2 } from 'lucide-react';

/**
 * ImageUpload — WhatsApp/Instagram style upload.
 * Click image → file picker → auto upload → onChange called with new URL.
 *
 * Props:
 *   value: current image URL (string)
 *   onChange: (url) => void  — called when upload finishes OR image removed
 *   folder: Cloudinary folder (default 'company')
 *   shape: 'circle' | 'square' | 'wide' | 'tall'
 *   label: small caption shown over the image
 *   placeholder: text when empty
 *   testId: data-testid prefix
 */
export const ImageUpload = ({
  value,
  onChange,
  folder = 'company',
  shape = 'square',
  label,
  placeholder = 'Adicionar imagem',
  testId = 'image-upload',
  className = '',
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const shapeClasses = {
    circle: 'aspect-square rounded-full',
    square: 'aspect-square rounded-2xl',
    wide: 'aspect-[3/1] rounded-2xl',
    tall: 'aspect-[3/4] rounded-2xl',
  }[shape] || 'aspect-square rounded-2xl';

  const handlePick = () => {
    if (uploading) return;
    inputRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione um arquivo de imagem.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Imagem muito grande (limite 8 MB).');
      return;
    }
    setUploading(true);
    try {
      const result = await uploadToCloudinary(file, 'image', folder);
      if (!result?.secure_url) throw new Error('Upload sem URL');
      onChange?.(result.secure_url);
      toast.success('Imagem atualizada!');
    } catch (err) {
      console.error(err);
      toast.error('Falha no upload. Tente novamente.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    onChange?.('');
  };

  return (
    <div className={`group relative ${className}`}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      <button
        type="button"
        onClick={handlePick}
        disabled={uploading}
        className={`relative w-full overflow-hidden border-2 border-dashed border-slate-300 hover:border-blue-500 transition-colors bg-slate-50 ${shapeClasses}`}
        data-testid={`${testId}-trigger`}
      >
        {value ? (
          <img src={value} alt={label || 'imagem'} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 gap-2 p-4">
            <ImagePlus className="w-8 h-8" />
            <span className="text-xs text-center">{placeholder}</span>
          </div>
        )}

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          {uploading ? (
            <Loader2 className="w-7 h-7 text-white animate-spin" />
          ) : (
            <div className="flex flex-col items-center gap-1 text-white">
              <Camera className="w-7 h-7" />
              <span className="text-xs font-medium">
                {value ? 'Trocar' : 'Selecionar'}
              </span>
            </div>
          )}
        </div>

        {uploading && !value && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
            <Loader2 className="w-7 h-7 text-blue-600 animate-spin" />
          </div>
        )}
      </button>

      {value && !uploading && (
        <button
          type="button"
          onClick={handleRemove}
          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white shadow-md flex items-center justify-center text-rose-600 hover:bg-rose-50 hover:scale-110 transition-all opacity-0 group-hover:opacity-100"
          aria-label="Remover imagem"
          data-testid={`${testId}-remove`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}

      {label && (
        <p className="text-xs text-slate-500 mt-2 text-center">{label}</p>
      )}
    </div>
  );
};

/**
 * Multi-image gallery uploader (for "fotos da empresa")
 *
 * Props:
 *   value: string[] — array of URLs
 *   onChange: (urls[]) => void
 *   folder
 *   max: maximum images (default 12)
 */
export const GalleryUpload = ({ value = [], onChange, folder = 'company-gallery', max = 12, testId = 'gallery' }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handlePick = () => {
    if (uploading) return;
    if (value.length >= max) {
      toast.error(`Limite de ${max} imagens atingido.`);
      return;
    }
    inputRef.current?.click();
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const remaining = max - value.length;
    const filesToUpload = files.slice(0, remaining);

    setUploading(true);
    const uploaded = [];
    for (const file of filesToUpload) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 8 * 1024 * 1024) {
        toast.error(`${file.name}: maior que 8 MB, ignorada.`);
        continue;
      }
      try {
        const res = await uploadToCloudinary(file, 'image', folder);
        if (res?.secure_url) uploaded.push(res.secure_url);
      } catch (err) {
        toast.error(`Falha em ${file.name}`);
      }
    }
    setUploading(false);
    if (uploaded.length) {
      onChange?.([...value, ...uploaded]);
      toast.success(`${uploaded.length} imagem(ns) adicionada(s).`);
    }
  };

  const remove = (idx) => {
    onChange?.(value.filter((_, i) => i !== idx));
  };

  return (
    <div data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFiles}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
        {value.map((url, idx) => (
          <div key={`${url}-${idx}`} className="relative group aspect-square rounded-xl overflow-hidden border border-slate-200">
            <img src={url} alt={`foto-${idx}`} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => remove(idx)}
              className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white shadow-md flex items-center justify-center text-rose-600 hover:bg-rose-50 opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Remover foto"
              data-testid={`${testId}-remove-${idx}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}

        {value.length < max && (
          <button
            type="button"
            onClick={handlePick}
            disabled={uploading}
            className="aspect-square rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-500 transition-colors flex flex-col items-center justify-center text-slate-400 gap-1 bg-slate-50"
            data-testid={`${testId}-add`}
          >
            {uploading ? (
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            ) : (
              <>
                <ImagePlus className="w-6 h-6" />
                <span className="text-xs">Adicionar</span>
              </>
            )}
          </button>
        )}
      </div>
      <p className="text-xs text-slate-500 mt-2">
        {value.length}/{max} imagens • Clique para adicionar (JPG/PNG até 8 MB)
      </p>
    </div>
  );
};

export default ImageUpload;
